import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import "./videoInfo.cmp.scss";
import DOMPurify from "dompurify";
import { getActiveIndex, getChapters } from "../utils/chapters";
import { getHost } from "../utils/host";
import IconPdf from "../icons/IconPdf";

const CmpVideoInfo = ({ onLinkClick }) => {
	const video = useSelector(state => state.video.currVideo);
	const currentTime = useSelector(state => state.video.currentTime);
	const tocRef = useRef();
	const sanitizedDescription = video ? DOMPurify.sanitize(video["description"] || "") : "";
  const sanitizedDescriptionIa = video && video["fromIa"] ? DOMPurify.sanitize(video["descriptionIa"] || "") : "";

  // Evidenzia capitolo e sezione in riproduzione nell'indice
  useEffect(() => {
    const toc = tocRef.current;
    if (!toc) return;

    const chapters = getChapters(video);
    const activeChapter = getActiveIndex(chapters, currentTime);
    const sections = chapters.flatMap(c => c.sections);
    const activeSection = getActiveIndex(sections, currentTime);

    toc.querySelectorAll("article").forEach((el, i) =>
      el.classList.toggle("active", i === activeChapter));
    toc.querySelectorAll("section a").forEach((el, i) =>
      el.classList.toggle("active", i === activeSection));
  }, [video, currentTime, sanitizedDescriptionIa]);

	const handleContainerClick = (e) => {
    const link = e.target.closest("a");
    if (!link) return;

    const href = link.getAttribute("href");
    if (!href) return;

    // Check if link matches /video format and parse params
    const parsedData = parseVideoLink(href);

    if (parsedData.shouldIntercept) {
      e.preventDefault(); // Prevent full page refresh
      
      // Pass full href plus parsed query params up to parent
      onLinkClick?.({
        href,
        id: parsedData.id,
        timestamp: parsedData.timestamp,
      });
    }
  };
	/**
   * Evaluates if href starts with /video and extracts URL parameters
   */
  const parseVideoLink = (href) => {
    try {
      // Pass window.location.origin as base to resolve relative paths like "/video?id=..."
      const url = new URL(href, window.location.origin);

      // 1. Check path matches "/video"
      const isVideoPath = url.pathname === "/video";

      // 2. Extract query parameters
      const id = url.searchParams.get("id");
      const timestamp = url.searchParams.get("t");

      return {
        shouldIntercept: isVideoPath && id !== null,
        id,
        timestamp,
      };
    } catch {
      return { shouldIntercept: false };
    }
  };

	/**
	 * Evaluates whether a link should be intercepted.
	 * Customize this condition based on your rules.
	 */
	const evaluateLink = (href, linkElement) => {
		// Example 1: Intercept internal site paths (e.g., starts with "/" or your domain)
		const isInternal = href.startsWith("/") || href.includes(window.location.hostname);
		
		// Example 2: Intercept specific hash/anchor tags or routes (e.g., "#timestamp", "/watch?v=")
		const isTimestamp = href.includes("#t=") || href.includes("timestamp");

		// Example 3: Check custom HTML attributes preserved by DOMPurify
		const isSpecialAction = linkElement.dataset.action === "custom-nav";

		return isInternal || isTimestamp || isSpecialAction;
	};

	const hasPdf = video && (video.pdfUrl || "").trim() !== "";

	return video ? <>
    <div className="spacer"></div>
    <div className = "cmp-video-info">
      <header className = "info-header">
        <h5 className = "title">{video.title}</h5>
        {
          hasPdf ?
          <a className = "pdf-link" href = {`${getHost()}${video.pdfUrl}`} target = "_blank" rel = "noopener noreferrer"
            title = "Manuale della lezione (PDF)">
            <IconPdf/>
            <span>PDF</span>
          </a> : null
        }
      </header>
      {
        video["fromIa"] ?
        <div className = "toc" ref = {tocRef} dangerouslySetInnerHTML = {{__html: sanitizedDescriptionIa}}
          onClick={handleContainerClick} /> :
        <div className = "description" dangerouslySetInnerHTML = {{__html: sanitizedDescription}}
          onClick={handleContainerClick} />
      }
    </div>
  </> : null
};

export default CmpVideoInfo;